import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaUser, FaBuilding, FaEdit, FaCheck, FaExclamationCircle, FaEnvelope } from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";
import Modal from "../../../components/Utils/ModalShow";
import { notify } from "../../../components/Utils/Notifications";

const UsersList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  
  // States for the edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [isMulticompany, setIsMulticompany] = useState(false);
  const [selectedRole, setSelectedRole] = useState("user");
  const [isSaving, setIsSaving] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Extract current user details
  const currentUserInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperadmin = currentUserInfo.role === 'superadmin';

  useEffect(() => {
    fetchUsers();
    // Load available companies from the current user session
    if (currentUserInfo && currentUserInfo.company_ids) {
       // Only standard business companies (skip ID 1 if possible, or filter it)
       const comps = currentUserInfo.company_ids
           .filter(c => c.id !== 1 && c.id !== '1')
           .map(c => ({...c, id: parseInt(c.id, 10)}));
       console.log("Filtered availableCompanies for admin:", comps);
       setAvailableCompanies(comps);
    }
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await apiService.getElement("/company/users");
      if (res.data && res.data.users) {
        setUsers(res.data.users);
      } else {
        setErrorMsg(t('usersList.fetchListError'));
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(t('usersList.loadError'));
    }
    setLoading(false);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    // Initialize selected companies based on what the user already has
    const userCompIds = user.companies ? user.companies.map(c => parseInt(c.id, 10)) : [];
    setSelectedCompanyIds(userCompIds);
    setIsMulticompany(!!user.multicompany);
    setSelectedRole(user.role || "user");
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setSelectedCompanyIds([]);
    setIsMulticompany(false);
    setSelectedRole("user");
  };

  const toggleCompanySelection = (companyId) => {
    const id = parseInt(companyId, 10);
    setSelectedCompanyIds(prev => {
      if (prev.includes(id)) {
         return prev.filter(cId => cId !== id);
      } else {
         return [...prev, id];
      }
    });
  };

  const handleSaveUserCompanies = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    try {
      console.log("Saving user companies:", selectedCompanyIds);
      const payload = { company_ids: selectedCompanyIds, multicompany: isMulticompany, role: selectedRole };
      const res = await apiService.saveElement(`/company/users/${selectedUser.id}/companies`, payload);
      if (res.data && res.data.success) {
         notify(t('usersList.accessUpdated'), false, 2);
         await fetchUsers(); // Refresh the list
         closeEditModal();
      } else {
         notify(res.data?.error || t('companySettings.updateError'), false, 3);
      }
    } catch (error) {
      console.error(error);
      const message = apiService.getApiErrorMessage(error, t('usersList.serverUpdateError'));
      notify(message, false, 3);
    }
    setIsSaving(false);
  };

  // Nouveau lien pour un utilisateur qui n'a pas encore activé son compte ;
  // il invalide le précédent (Yros/payor_enterprise#319).
  const handleResendInvitation = async (user) => {
    setResendingId(user.id);
    try {
      const pays = localStorage.getItem('selectedCountryCode') || 'sn';
      const res = await apiService.saveElement(`/company/users/${user.id}/invitation`, { pays });
      if (res.data?.success) {
        notify(t('usersList.invitationResent', { email: user.email }), false, 2);
      } else {
        notify(res.data?.error || t('usersList.invitationResendError'), false, 3);
      }
    } catch (error) {
      // 400 / 429 (renvoi trop rapproché) : le serveur explique le refus.
      const raison = error.response?.status !== 403 ? error.response?.data?.error : null;
      notify(
        typeof raison === 'string' ? raison : apiService.getApiErrorMessage(error, t('usersList.invitationResendError')),
        false,
        3
      );
    }
    setResendingId(null);
  };

  return (
    <div className="">
      <div className="flex items-center gap-3 mb-6">
          <div className="bg-[--color-primary] rounded-full p-2">
              <FaUser className="text-white" size={16} />
          </div>
          <h4 className="text-lg font-semibold text-gray-800">{t('usersList.title')}</h4>
      </div>

      {errorMsg && (
          <div className="flex items-center gap-3 p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">
              <FaExclamationCircle className="text-red-600" size={16} />
              <span className="font-medium">{errorMsg}</span>
          </div>
      )}

      {loading ? (
          <div className="flex justify-center items-center py-10">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[--color-primary]"></div>
             <span className="ml-3 text-gray-600">{t('usersList.loadingUsers')}</span>
          </div>
      ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
             <table className="min-w-full divide-y divide-gray-200">
                 <thead className="bg-gray-50">
                     <tr>
                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                             {t('usersList.user')}
                         </th>
                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                             {t('settingsPage.role')}
                         </th>
                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                             {t('usersList.accessibleCompanies')}
                         </th>
                         <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                             {t('absenceType.actions')}
                         </th>
                     </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-gray-200">
                     {users.length > 0 ? users.map(u => (
                         <tr key={u.id}>
                             <td className="px-6 py-4 whitespace-nowrap">
                                 <div className="flex items-center">
                                     <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                                         <FaUser />
                                     </div>
                                     <div className="ml-4">
                                         <div className="text-sm font-medium text-gray-900">{u.name}</div>
                                         <div className="text-sm text-gray-500">{u.email}</div>
                                         {u.invitation_pending && (
                                             <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200" data-cy="user-invitation-pending">
                                                 {t('usersList.invitationPending')}
                                             </span>
                                         )}
                                     </div>
                                 </div>
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap">
                                 {u.role === 'superadmin' ? (
                                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                         {t('usersList.superAdministrator')}
                                     </span>
                                 ) : u.role === 'admin' ? (
                                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                         {t('settingsPage.administrator')}
                                     </span>
                                 ) : u.role === 'rh' ? (
                                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                         {t('usersList.hr')}
                                     </span>
                                 ) : u.role === 'compta' ? (
                                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                         {t('usersList.accountant')}
                                     </span>
                                 ) : (
                                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                         {t('usersList.client')}
                                     </span>
                                 )}
                             </td>
                             <td className="px-6 py-4">
                                 <div className="flex flex-wrap gap-2">
                                     {u.companies && u.companies.length > 0 ? u.companies.map(c => (
                                         <span key={c.id} className="inline-flex items-center px-2.5 py-0.5 border border-[--color-primary] rounded-full text-xs font-medium bg-orange-50 text-[--color-primary]">
                                            {c.name}
                                         </span>
                                     )) : (
                                         <span className="text-sm text-gray-400 italic">{t('usersList.noCompany')}</span>
                                     )}
                                 </div>
                             </td>
                             <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                 {u.invitation_pending && (
                                     <button
                                         onClick={() => handleResendInvitation(u)}
                                         disabled={resendingId === u.id}
                                         className="text-[--color-primary] hover:opacity-80 bg-orange-50 hover:bg-orange-100 px-3 py-1 rounded-md transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                                         data-cy="user-resend-invitation"
                                     >
                                         <FaEnvelope /> {t('usersList.resendInvitation')}
                                     </button>
                                 )}
                                 <button
                                     onClick={() => openEditModal(u)}
                                     className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition-colors inline-flex items-center gap-2"
                                 >
                                     <FaEdit /> {t('absenceType.edit')}
                                 </button>
                             </td>
                         </tr>
                     )) : (
                        <tr>
                            <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                {t('usersList.noUserFound')}
                            </td>
                        </tr>
                     )}
                 </tbody>
             </table>
          </div>
      )}

      {/* Modal for editing access */}
      <Modal
        isOpen={showEditModal}
        onClose={closeEditModal}
        title={t('usersList.editCompanyAccess')}
        headerIcon={<FaBuilding />}
        footer={
           <>
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 mr-2 hover:bg-gray-300 transition-colors"
                onClick={closeEditModal}
                type="button"
                disabled={isSaving}
              >
                {t('basePrimeForm.cancel')}
              </button>
              <button
                className="px-4 py-2 rounded bg-[--color-primary] text-white hover:opacity-90 flex items-center gap-2 transition-opacity"
                onClick={handleSaveUserCompanies}
                type="button"
                disabled={isSaving}
              >
                {isSaving ? (
                     <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {t('companySettings.saving')}
                     </>
                ) : (
                     <>
                        <FaCheck size={16} /> {t('usersList.save')}
                     </>
                )}
              </button>
           </>
        }
      >
        <div className="p-2 space-y-4">
            <h4 className="font-medium text-gray-800">
                {t('usersList.userLabel')} <span className="font-normal text-gray-600">{selectedUser?.name} ({selectedUser?.email})</span>
            </h4>

            <div className="mt-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('usersList.userRole')}
                </label>
                {selectedUser?.role === 'superadmin' ? (
                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 text-sm">
                        {t('usersList.superAdministrator')}
                    </div>
                ) : (
                    <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-[--color-primary] focus:border-[--color-primary] sm:text-sm rounded-md"
                    >
                        <option value="user">{t('settingsPage.clientRole')}</option>
                        <option value="compta">{t('settingsPage.accountantRole')}</option>
                        <option value="rh">{t('settingsPage.hrRole')}</option>
                        {(isSuperadmin || currentUserInfo.role === 'admin') && <option value="admin">{t('settingsPage.administrator')}</option>}
                    </select>
                )}
            </div>

            <p className="text-sm text-gray-600 mb-4 mt-4">
                {t('usersList.selectCompaniesHint')}
            </p>

            <div className="mb-4 p-3 bg-gray-50 border rounded-lg">
                <label className="flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={isMulticompany}
                        onChange={(e) => setIsMulticompany(e.target.checked)}
                        className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                    />
                    <div className="ml-3">
                        <span className="text-gray-800 font-medium block">{t('usersList.enableMulticompany')}</span>
                        <span className="text-xs text-gray-500">{t('usersList.enableMulticompanyHint')}</span>
                    </div>
                </label>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {availableCompanies.length > 0 ? availableCompanies.map(company => (
                    <label key={company.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                        <input
                            type="checkbox"
                            checked={selectedCompanyIds.includes(company.id)}
                            onChange={() => toggleCompanySelection(company.id)}
                            className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                        />
                        <span className="ml-3 text-gray-800 font-medium">
                            {company.name}
                        </span>
                    </label>
                )) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                        {t('usersList.noCompanyAvailable')}
                    </div>
                )}
            </div>
        </div>
      </Modal>

    </div>
  );
};

export default UsersList;
